import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { MailOpen, ChevronRight } from 'lucide-react'
import { getDirectoryPostcodes, getDirectoryPlumbersForTown, getDirectoryTowns, type DirectoryExtraFilters } from '../../lib/api'
import { DIRECTORY_PLUMBERS_PAGE_SIZE, DIRECTORY_MAP_PLUMBER_LIMIT } from '../../lib/directoryConstants'
import { useDirectoryPlumbersInfinite } from '../../hooks/useDirectoryPlumbersInfinite'
import { useDirectoryMode } from '../../hooks/useDirectoryMode'
import { PageWrapper } from '../../components/layout/PageWrapper'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Seo } from '../../components/Seo'
import { directoryCanonicalPath } from '../../lib/directoryCanonical'
import { PostcodeCardGridSkeleton } from '../../components/ui/Skeletons'
import { DirectorySidebar } from '../../components/directory/DirectorySidebar'
import { DirectoryPlumberList } from '../../components/directory/DirectoryPlumberList'
import { DIRECTORY_MAIN_GRID, DIRECTORY_PAGE_MAX, DIRECTORY_SIDEBAR_COL, slugToLabel } from './directoryUtils'
import { DirectorySearchBar } from '../../components/directory/DirectorySearchBar'
import { buildPlumberHref, toPlumberPins } from '../../lib/plumberPins'

export function DirectoryTown() {
  const { countrySlug, countySlug, townSlug } = useParams<{
    countrySlug: string
    countySlug: string
    townSlug: string
  }>()

  const { isEmergency, isGasSafe, isWaterSafe, basePath, label } = useDirectoryMode()
  const [extraFilters, setExtraFilters] = useState<DirectoryExtraFilters>({})

  const { data: postcodes, isLoading: loadingPostcodes } = useQuery({
    queryKey: ['directory', 'postcodes', countrySlug, countySlug, townSlug, isEmergency, isGasSafe, isWaterSafe],
    queryFn: () => getDirectoryPostcodes(countrySlug!, countySlug!, townSlug!, isEmergency, isGasSafe, isWaterSafe),
    enabled: !!(countrySlug && countySlug && townSlug),
  })

  const fetchPlumbers = useCallback(
    (skip: number) =>
      getDirectoryPlumbersForTown(countrySlug!, countySlug!, townSlug!, isEmergency, isGasSafe, isWaterSafe, {
        skip,
        limit: DIRECTORY_PLUMBERS_PAGE_SIZE,
      }, extraFilters),
    [countrySlug, countySlug, townSlug, isEmergency, isGasSafe, isWaterSafe, extraFilters],
  )

  const {
    data: plumberPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: loadingPlumbers,
  } = useDirectoryPlumbersInfinite(
    ['directory', 'plumbers', 'town', countrySlug, countySlug, townSlug, isEmergency, isGasSafe, isWaterSafe, extraFilters],
    fetchPlumbers,
    !!(countrySlug && countySlug && townSlug),
  )

  const plumbers = plumberPages?.pages.flatMap(p => p.plumbers) ?? []
  const listTotal = plumberPages?.pages[0]?.total
  const emergencyTotal = plumberPages?.pages[0]?.emergencyTotal ?? 0

  const countryLabel = slugToLabel(countrySlug)
  const countyLabel  = slugToLabel(countySlug)
  const townLabel    = slugToLabel(townSlug)

  const hrefCtx = { basePath, countrySlug: countrySlug!, countySlug: countySlug!, townSlug: townSlug! }

  // Sibling towns in the same county — nearby-area cross-links.
  const { data: siblingTowns } = useQuery({
    queryKey: ['directory', 'sibling-towns', countrySlug, countySlug],
    queryFn: () => getDirectoryTowns(countrySlug!, countySlug!),
    enabled: !!(countrySlug && countySlug),
    staleTime: 5 * 60 * 1000,
  })
  const nearbyTownLinks = (siblingTowns ?? [])
    .filter(t => t.townSlug !== townSlug)
    .slice(0, 12)
    .map(t => ({ label: t.town, href: `${basePath}/${countrySlug}/${countySlug}/${t.townSlug}` }))

  const { data: mapData } = useQuery({
    queryKey: ['directory', 'plumbers-map', 'town', countrySlug, countySlug, townSlug, isEmergency, isGasSafe, isWaterSafe],
    queryFn: () => getDirectoryPlumbersForTown(countrySlug!, countySlug!, townSlug!, isEmergency, isGasSafe, isWaterSafe, { skip: 0, limit: DIRECTORY_MAP_PLUMBER_LIMIT }, {}),
    enabled: !!(countrySlug && countySlug && townSlug),
    staleTime: 5 * 60 * 1000,
  })
  const plumberPins = useMemo(
    () => toPlumberPins(mapData?.plumbers ?? [], p => buildPlumberHref(p, hrefCtx)),
    [mapData, basePath, countrySlug, countySlug, townSlug],
  )

  const faqs = [
    {
      q: `How do I find ${isEmergency ? 'an emergency plumber' : isGasSafe ? 'a Gas Safe plumber' : isWaterSafe ? 'a WaterSafe plumber' : 'a plumber'} in ${townLabel}?`,
      a: `Browse${listTotal ? ` our ${listTotal} listed plumbers` : ' plumbers'} in ${townLabel} by postcode. For urgent callouts, use FixDelivered's emergency service for fastest dispatch.`,
    },
    {
      q: `What plumbing services are available in ${townLabel}?`,
      a: `Full range: emergency callouts, boiler repair, central heating, burst pipes, blocked drains, leak detection, gas safety certificates, and bathroom installations.`,
    },
  ]

  return (
    <PageWrapper>
      <Seo
        title={`${label} in ${townLabel}, ${countyLabel} | FixDelivered`}
        description={`${listTotal ? `${listTotal} verified` : 'Find'} ${label.toLowerCase()} in ${townLabel}, ${countyLabel} — compare ratings & call-out fees, or let FixDelivered confirm an available plumber, usually within the hour.`}
        path={directoryCanonicalPath(countrySlug, countySlug, townSlug)}
        noindex={listTotal === 0}
        breadcrumbs={[
          { name: 'UK', url: basePath },
          { name: countryLabel, url: `${basePath}/${countrySlug}` },
          { name: countyLabel, url: `${basePath}/${countrySlug}/${countySlug}` },
          { name: townLabel, url: `${basePath}/${countrySlug}/${countySlug}/${townSlug}` },
        ]}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: `Plumber Matching in ${townLabel}`,
            provider: { '@type': 'Organization', name: 'FixDelivered', url: 'https://fixdelivered.com' },
            serviceType: 'Emergency Plumbing',
            areaServed: { '@type': 'Place', name: `${townLabel}, ${countyLabel}` },
          },
          ...(plumbers.length ? [{
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: `Plumbers in ${townLabel}`,
            itemListElement: plumbers.slice(0, 25).map((p, i) => ({
              '@type': 'ListItem', position: i + 1,
              url: `https://fixdelivered.com${buildPlumberHref(p, hrefCtx)}`,
              name: p.companyName,
            })),
          }] : []),
        ]}
      />

      <div className={DIRECTORY_PAGE_MAX}>
        <div className={DIRECTORY_MAIN_GRID}>
          <div className="min-w-0">
            <Breadcrumb items={[
              { label: countryLabel, href: `${basePath}/${countrySlug}` },
              { label: countyLabel, href: `${basePath}/${countrySlug}/${countySlug}` },
              { label: townLabel },
            ]} />

            <div className="mt-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-900">{label} in {townLabel}</h1>
              <p className="text-gray-500 mt-1 text-sm min-h-[2.75rem] sm:min-h-[1.5rem]">
                {listTotal && listTotal > 0
                  ? `${listTotal} verified plumbers${emergencyTotal > 0 ? ` · ${emergencyTotal} available 24/7` : ''}. `
                  : ''}
                Select a postcode area or browse plumbers below.
              </p>
              <DirectorySearchBar className="mt-3" />
            </div>

            {loadingPostcodes ? (
              <PostcodeCardGridSkeleton count={6} />
            ) : postcodes?.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {postcodes.map(p => (
                  <Link
                    key={p.postcodeSlug}
                    to={`${basePath}/${countrySlug}/${countySlug}/${townSlug}/${p.postcodeSlug}`}
                    className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                        <MailOpen className="h-3.5 w-3.5 text-gray-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 uppercase text-sm">{p.postcodeArea}</p>
                        <p className="text-xs text-gray-400">{p.count} plumber{p.count !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-trust-400 transition-colors" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 text-gray-400">
                <p>No postcodes found for this area.</p>
                <Link to={`${basePath}/${countrySlug}/${countySlug}`} className="text-trust-500 hover:underline text-sm mt-2 inline-block">
                  Back to {countyLabel}
                </Link>
              </div>
            )}

            <DirectoryPlumberList
              plumbers={plumbers}
              isLoading={loadingPlumbers}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              listTotal={listTotal}
              extraFilters={extraFilters}
              onFiltersChange={setExtraFilters}
              hrefBuilder={p => buildPlumberHref(p, hrefCtx)}
              heading={`Plumbers in ${townLabel}`}
              emptyAreaLabel="this area"
            />
          </div>

          <div className={DIRECTORY_SIDEBAR_COL}>
            <DirectorySidebar
              count={listTotal}
              areaLabel={townLabel}
              emergencyCount={emergencyTotal}
              plumberPins={plumberPins}
              nearby={{ title: `Nearby towns in ${countyLabel}`, links: nearbyTownLinks }}
            />
          </div>
        </div>
      </div>
    </PageWrapper>
  )
}
